import { CanvasSelect } from "@/features/canvas/components/CanvasSelect";
import type { Match } from "@/lib/types";

/**
 * Match + slot picker for Coach Review's VIDEO mode.
 *
 * A video coaching note is anchored to a specific match + slot
 * (+ timestamp). In M4 there's no HLS player yet, so the coach
 * picks the match and slot here; `timestamp_seconds` is sent as 0
 * ("start of the slot") until M5's player lets them scrub. The
 * backend's `CreateCoachingNoteRequest` per-context validator
 * requires `match_id` + `slot` + `timestamp_seconds` for video
 * notes — all three must be present, and `0` is a valid
 * timestamp (`ge=0`).
 *
 * Tactical-board mode does NOT render this — observations carry
 * no match anchor. The parent shows it only when `source ===
 * "video"`.
 *
 * The match list comes from `useMatches()` — TEAM-wide, across
 * every season (`getMatches()` suppresses `season_id`; see
 * tktic#8). A coach can attach a note to any of their team's
 * matches, not just the active season's. When the route already
 * carries `?match_id=`, the bundle resolved that match and the
 * parent seeds `matchId` here so the dropdown opens on it.
 */

/** The three standard slots — mirrors `_VALID_SLOTS` in models.py. */
const SLOTS: ReadonlyArray<{ value: string; label: string }> = [
  { value: "full", label: "Full match" },
  { value: "first_half", label: "1st half" },
  { value: "second_half", label: "2nd half" },
];

export type MatchSlotPickerProps = {
  matches: Match[];
  matchesLoading: boolean;
  /** Selected match id, or "" when none picked yet. */
  matchId: string;
  onMatchIdChange: (id: string) => void;
  /** Selected slot — one of `_VALID_SLOTS`. */
  slot: string;
  onSlotChange: (slot: string) => void;
  /** When true, both selects are locked. Coach Review sets this in
   * clip-EDIT mode — a clip's match/slot anchor is immutable
   * (`UpdateCoachingClipRequest` is `extra="forbid"` and omits
   * them), so the picker must not let the coach change it. */
  disabled?: boolean;
};

/** Build the display label for a match. Exported so the clip
 * composer's read-only anchor banner uses the same format. */
export function matchLabel(m: Match): string {
  const teams = `${m.home_team} vs ${m.away_team}`;
  return m.date ? `${teams} · ${m.date}` : teams;
}

export function MatchSlotPicker({
  matches,
  matchesLoading,
  matchId,
  onMatchIdChange,
  slot,
  onSlotChange,
  disabled = false,
}: MatchSlotPickerProps) {
  return (
    <div
      className="flex flex-col gap-3"
      data-testid="coach-review-match-slot-picker"
    >
      <label className="flex flex-col gap-1">
        <span className="text-xs font-display uppercase tracking-wider text-ink-muted">
          Match
        </span>
        <CanvasSelect
          value={matchId}
          onChange={(e) => onMatchIdChange(e.target.value)}
          disabled={matchesLoading || disabled}
          data-testid="composer-match"
        >
          <option value="">
            {matchesLoading ? "Loading matches…" : "Select a match…"}
          </option>
          {matches.map((m) => (
            <option key={m.id} value={m.id}>
              {matchLabel(m)}
            </option>
          ))}
        </CanvasSelect>
      </label>
      <label className="flex flex-col gap-1">
        <span className="text-xs font-display uppercase tracking-wider text-ink-muted">
          Slot
        </span>
        <CanvasSelect
          value={slot}
          onChange={(e) => onSlotChange(e.target.value)}
          disabled={disabled}
          data-testid="composer-slot"
        >
          {SLOTS.map((s) => (
            <option key={s.value} value={s.value}>
              {s.label}
            </option>
          ))}
        </CanvasSelect>
      </label>
      {!matchesLoading && matches.length === 0 ? (
        <p className="text-xs text-ink-muted">
          No matches in this team/season scope yet. Add a match in Admin first.
        </p>
      ) : null}
    </div>
  );
}
